import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { APP_TOAST_KEY } from "@kno2/common/services";
import { APP_ORIGIN_NAME_TOKEN } from "@kno2/core/configuration";
import { ApplicationInsightsService, IntercomService, IntercomSettings } from "@kno2/core/integrations";
import { AuthSliceFacade } from "@kno2/data-access/+store";
import { AuthService } from "@kno2/data-access/api-desktop";
import { FeaturesService, IntegrationsService, OrganizationSummaryResource, ProfileService, ReleaseTypesService } from "@kno2/data-access/api-kno2fy-main";
import { BehaviorSubject, Subscription, of, combineLatest, from } from "rxjs";
import { filter, switchMap, take, tap } from "rxjs/operators";

@Component({
    selector: "kno2-app",
    template: `
        <p-toast
            [key]="toastKey"
            position="top-center" />
        @if ((appReady$ | async) || (userReady$ | async)) {
            <div>
                <ng-container *ngTemplateOutlet="template"></ng-container>
            </div>
        } @else {
            <div
                class="flex justify-center items-center"
                style="height: 100vh;">
                <p-progress-spinner ariaLabel="Loading" />
            </div>
        }
        <ng-template #template>
            <router-outlet></router-outlet>
        </ng-template>
    `,
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
    protected readonly toastKey = APP_TOAST_KEY;
    public appReady$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public userReady$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private subscriptions: Array<Subscription> = [];

    constructor(
        private appInsights: ApplicationInsightsService,
        private intercomService: IntercomService,
        private profileService: ProfileService,
        private featuresService: FeaturesService,
        private integrationsService: IntegrationsService,
        private authSlice: AuthSliceFacade,
        private authService: AuthService,
        private releaseTypesService: ReleaseTypesService,
        @Inject(APP_ORIGIN_NAME_TOKEN) private appName: string
    ) {}

    public ngOnInit(): void {
        const profileInitializationSub = this.authSlice.authToken$
            .pipe(
                filter((token) => !!token),
                switchMap((existingToken) =>
                    combineLatest([this.profileService.getMergedProfileData$(), this.profileService.getActiveOrganization$(), of(existingToken)])
                ),
                tap((data) => {
                    // ensure that appReady only emits once
                    if (!this.appReady$.value && !data) this.appReady$.next(true);
                })
            )
            .subscribe(([user, activeOrganization, token]) => {
                this.initializeIntegrations(user, token, activeOrganization);
                this.initializeFeatures(user);
                this.initializeCapabilities();
                this.intializeReleaseTypes();

                this.userReady$.next(true);
            });

        const authToken = this.authService.accessToken$().subscribe((token) => {
            this.authSlice.setAuthToken(token);
        });

        this.subscriptions.push(profileInitializationSub, authToken);
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach((x) => x.unsubscribe());
    }

    private initializeIntegrations(user: any, authToken: string, organization: OrganizationSummaryResource): void {
        this.appInsights.setUserContext(user.userId, user.organizationId);
        this.initializeIntercom(user);
    }

    private initializeIntercom(user: any): void {
        if (!user.intercomAppId || !user.userId || !user.intercomUserJwt) return;

        from(window.api.getDebugInfo())
            .pipe(
                take(1),
                tap(({ payload }) => {
                    const settings: IntercomSettings = {
                        app_id: user.intercomAppId,
                        user_id: user.userId,
                        intercom_user_jwt: user.intercomUserJwt,
                        alignment: "right",
                        ...(user.kno2OrganizationId != null
                            ? {
                                  company: {
                                      company_id: String(user.kno2OrganizationId),
                                      name: user.organizationName
                                  }
                              }
                            : {})
                    };
                    this.intercomService.boot(settings, {
                        Application: this.appName,
                        "Session ID": payload.sessionId,
                        "App Version": payload.appVersion,
                        "Auto Update Enabled": payload.isAutoUpdateEnabled ? "yes" : "no"
                    });
                })
            )
            .subscribe();
    }

    private initializeFeatures(user: any): void {
        this.subscriptions.push(this.featuresService.getEnabledFeatures$().subscribe());
    }

    private initializeCapabilities(): void {
        this.subscriptions.push(this.integrationsService.getCapabilities$().subscribe());
    }

    private intializeReleaseTypes(): void {
        this.subscriptions.push(this.releaseTypesService.getReleaseTypes$().subscribe());
    }
}
